import { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Checkbox, Dropdown, Input, MessageBarBody, MessageBarTitle, Option } from '@fluentui/react-components';
import { useTranslation } from 'react-i18next';

// models
import { Person } from '../../../models/Entities/Person/Person';
import {
  PersonRelationshipCreateCommand,
  PersonRelationshipUpdateCommand
} from '../../../models/Entities/Person/Relationship/PersonRelationshipModels';

// components
import { ErrorMessageComponent } from '../../Error/ErrorMessageComponent';
import { GuideDrawer, GuideDrawerForm } from '../../../theme/components/GuideDrawer';
import { GuideField, GuideFieldGrid } from '../../../theme/components/GuideField';
import { GuideMessageBar } from '../../../theme/components/GuideMessageBar';
import RelationshipTypeSelectorComponent from '../Selectors/RelationshipTypeSelector/RelationshipTypeSelector.Component';

// functions
import * as CommonFunctions from '../Persons.Functions';
import * as ownFunctions from './PersonRelationship.Form.Functions';

interface FormValidation {
  success: boolean;
  errors: string[];
}

const PersonRelationshipFormComponent = (props: ownFunctions.Props) => {
  const [t] = useTranslation();

  const [submitted, setSubmitted] = useState<boolean>(false);
  const [validation, setValidation] = useState<FormValidation | undefined>();

  const [relatedPersonId, setRelatedPersonId] = useState<number | undefined>(undefined);
  const [relationshipTypeId, setRelationshipTypeId] = useState<number | undefined>(undefined);
  const [isActive, setIsActive] = useState<boolean>(true);
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');

  useEffect(() => {
    props.GetAllPersons();
  }, []);

  useEffect(() => {
    if (props.relationship) {
      setRelatedPersonId(props.relationship.relatedPerson?.id);
      setRelationshipTypeId(props.relationship.relationshipType?.id);
      setIsActive(props.relationship.isActive ?? true);
      setStartDate(CommonFunctions.ToInputDate(props.relationship.startDate));
      setEndDate(CommonFunctions.ToInputDate(props.relationship.endDate));
    } else {
      resetForm();
    }
  }, [props.relationship, props.isOpen]);

  useEffect(() => {
    if (submitted && (props.successAdding || props.successUpdating)) {
      resetForm();
      props.dismissPanel();
    }
  }, [props.successAdding, props.successUpdating]);

  const resetForm = () => {
    setRelatedPersonId(undefined);
    setRelationshipTypeId(undefined);
    setIsActive(true);
    setStartDate('');
    setEndDate('');
    setSubmitted(false);
    setValidation(undefined);
  };

  const validate = (): boolean => {
    const errors: string[] = [];

    if (!relatedPersonId) {
      errors.push(
        t('persons.relationships.form.validation.relatedPerson', {
          defaultValue: 'La persona relacionada es obligatoria'
        })
      );
    }

    if (!relationshipTypeId) {
      errors.push(
        t('persons.relationships.form.validation.relationshipType', {
          defaultValue: 'El tipo de relación es obligatorio'
        })
      );
    }

    if (startDate && endDate && new Date(endDate) < new Date(startDate)) {
      errors.push(
        t('persons.relationships.form.validation.dates', {
          defaultValue: 'La fecha de fin debe ser posterior a la de inicio'
        })
      );
    }

    const result: FormValidation = { success: errors.length === 0, errors };
    setValidation(result);
    return result.success;
  };

  const handleSave = () => {
    setSubmitted(true);
    if (!validate()) return;

    const payload: PersonRelationshipCreateCommand = {
      personId: props.PersonId,
      relatedPersonId: relatedPersonId!,
      relationshipTypeId: relationshipTypeId!,
      isActive,
      startDate: CommonFunctions.FromInputDate(startDate),
      endDate: CommonFunctions.FromInputDate(endDate)
    };

    if (props.relationship) {
      props.UpdatePersonRelationship({
        ...payload,
        id: props.relationship.id
      } as PersonRelationshipUpdateCommand);
    } else {
      props.CreatePersonRelationship(payload);
    }
  };

  const handleCancel = () => {
    props.dismissPanel();
    resetForm();
  };

  const selectedRelatedPerson = props.availablePersons.find(
    (person: Person) => person.id === relatedPersonId
  );

  const isSaving = !!(props.isAdding || props.isUpdating);

  return (
    <GuideDrawer
      open={props.isOpen}
      size="medium"
      eyebrow={props.personName || undefined}
      title={
        props.relationship
          ? t('persons.relationships.form.editTitle', { defaultValue: 'Editar relación' })
          : t('persons.relationships.form.addTitle', { defaultValue: 'Nueva relación' })
      }
      onDismiss={handleCancel}
      preventDismiss={isSaving}
      testId="person-relationship-drawer"
      secondaryAction={{ label: t('persons.form.footer.cancel', { defaultValue: 'Cancelar' }) }}
      primaryAction={{
        label: t('persons.form.footer.save', { defaultValue: 'Guardar' }),
        onClick: handleSave,
        loading: isSaving
      }}
    >
      {submitted && validation && !validation.success && (
        <GuideMessageBar intent="error" layout="multiline">
          <MessageBarBody>
            <MessageBarTitle>
              {t('persons.form.review', { defaultValue: 'Revise la información ingresada' })}
            </MessageBarTitle>
            <ul>
              {validation.errors.map((error: string, index: number) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          </MessageBarBody>
        </GuideMessageBar>
      )}

      {submitted && (props.failAdding || props.failUpdating) && props.error && (
        <ErrorMessageComponent Error={props.error} />
      )}

      <GuideDrawerForm onSubmit={handleSave}>
        <GuideField
          label={t('persons.relationships.form.relatedPerson', { defaultValue: 'Persona relacionada' })}
          required
          error={
            submitted && !relatedPersonId
              ? t('persons.relationships.form.validation.relatedPerson', {
                  defaultValue: 'La persona relacionada es obligatoria'
                })
              : undefined
          }
        >
          <Dropdown
            placeholder={t('persons.relationships.form.relatedPersonPlaceholder', {
              defaultValue: 'Seleccione una persona'
            })}
            value={CommonFunctions.GetPersonDisplayName(selectedRelatedPerson)}
            selectedOptions={relatedPersonId ? [String(relatedPersonId)] : []}
            onOptionSelect={(_, data) =>
              setRelatedPersonId(data.optionValue ? Number(data.optionValue) : undefined)
            }
          >
            {props.availablePersons.map((person: Person) => (
              <Option
                key={person.id}
                value={String(person.id)}
                text={CommonFunctions.GetPersonDisplayName(person)}
              >
                {CommonFunctions.GetPersonDisplayName(person)}
              </Option>
            ))}
          </Dropdown>
        </GuideField>

        <RelationshipTypeSelectorComponent
          selectedRelationshipTypeId={relationshipTypeId}
          personType={props.PersonType}
          isRequired
          errorMessage={
            submitted && !relationshipTypeId
              ? t('persons.relationships.form.validation.relationshipType', {
                  defaultValue: 'El tipo de relación es obligatorio'
                })
              : undefined
          }
          onSelectionChange={(value: number | undefined) => setRelationshipTypeId(value)}
        />

        <GuideFieldGrid>
          <GuideField label={t('persons.relationships.form.startDate', { defaultValue: 'Fecha de inicio' })}>
            <Input type="date" value={startDate} onChange={(_, data) => setStartDate(data.value || '')} />
          </GuideField>

          <GuideField label={t('persons.relationships.form.endDate', { defaultValue: 'Fecha de fin' })}>
            <Input type="date" value={endDate} onChange={(_, data) => setEndDate(data.value || '')} />
          </GuideField>
        </GuideFieldGrid>

        <Checkbox
          label={t('persons.relationships.form.isActive', { defaultValue: 'Relación activa' })}
          checked={isActive}
          onChange={(_, data) => setIsActive(!!data.checked)}
        />
      </GuideDrawerForm>
    </GuideDrawer>
  );
};

export default connect(
  ownFunctions.mapStateToProps,
  ownFunctions.mapDispatchToProps
)(PersonRelationshipFormComponent as any);
